import { makeAutoObservable, runInAction } from 'mobx';
import type { BibleLocation } from '@skjv/common';
import { formatReference, getBookInfo, isLocationEqual } from '@skjv/common';
import { saveLocationToHash, loadLocationFromHash, saveHistory, loadHistory } from '../persistence';

/**
 * Navigation state - current location and reading history.
 */
export class NavigationState {
  /** Current location in the Bible (all indices are 0-based) */
  currentLocation: BibleLocation = {
    testamentIndex: 0,
    bookIndex: 0,
    chapterIndex: 0,
    verseIndex: 0,
  };

  /** Reading history (max 40 entries) */
  bibleHistory: BibleLocation[] = [];

  /** Flag to indicate location change was from scrolling */
  isScrollUpdate = false;

  /** Loading state */
  isLoading = false;

  constructor() {
    makeAutoObservable(this);

    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', () => {
        const location = loadLocationFromHash();
        if (location) {
          runInAction(() => {
            this.currentLocation = location;
          });
        }
      });

      this.bibleHistory = loadHistory<BibleLocation>();

      const initialLocation = loadLocationFromHash();
      if (initialLocation) {
        this.currentLocation = initialLocation;
      }
    }
  }

  /** Get formatted current reference */
  get currentReference(): string {
    return formatReference(this.currentLocation);
  }

  /** Get current book name */
  get currentBookName(): string {
    const book = getBookInfo(this.currentLocation.testamentIndex, this.currentLocation.bookIndex);
    return book?.name ?? 'Unknown';
  }

  /** Get current chapter number (1-based for display) */
  get currentChapterNumber(): number {
    return this.currentLocation.chapterIndex + 1;
  }

  /** Prepend a location to history without navigating to it. */
  addLocationToHistory(location: BibleLocation): void {
    if (this.bibleHistory.length > 0 && isLocationEqual(this.bibleHistory[0], location)) return;
    this.bibleHistory = [location, ...this.bibleHistory].slice(0, 40);
    saveHistory(this.bibleHistory);
  }

  /** Update current location (adds to history) */
  setCurrentLocation(location: BibleLocation): void {
    console.log('[NavState] setCurrentLocation:', location);
    if (this.bibleHistory.length === 0 || !isLocationEqual(this.bibleHistory[0], location)) {
      this.bibleHistory = [location, ...this.bibleHistory].slice(0, 40);
      saveHistory(this.bibleHistory);
    }
    this.currentLocation = location;
    saveLocationToHash(location);
    console.log('[NavState] URL hash updated, currentLocation now:', this.currentLocation);
  }

  /** Update location from scroll (won't trigger navigation in BibleReader) */
  setLocationFromScroll(location: BibleLocation): void {
    this.isScrollUpdate = true;
    this.currentLocation = location;
    saveLocationToHash(location);
    setTimeout(() => {
      runInAction(() => {
        this.isScrollUpdate = false;
      });
    }, 0);
  }

  /** Update display location only (no history save) */
  updateDisplayLocation(location: BibleLocation): void {
    this.currentLocation = location;
  }
}
